<template>
  <div v-if="isLoading" class="flex items-center justify-center bg-gradient-to-b from-slate-700 to-slate-900">
    <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
  </div>

  <div v-else class="p-2 md:p-8 bg-gradient-to-b from-slate-700 to-slate-900">
    <!-- User Profile Section -->
    <div class="max-w-4xl mx-auto">
      <!-- Enhanced Hero Section with Gradient -->
      <div class="relative overflow-hidden rounded-2xl mb-8 bg-gradient-to-br from-purple-900 via-slate-900 to-slate-800">
        <!-- Decorative elements -->
        <div class="absolute inset-0">
          <div class="absolute -top-24 -left-24 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob"></div>
          <div class="absolute -bottom-24 -right-24 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-2xl opacity-20 animate-blob animation-delay-2000"></div>
        </div>

        <div class="relative p-6 md:p-8">
          <div class="flex flex-col md:flex-row items-center gap-6">
            <!-- Profile Avatar -->
            <div class="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-3xl md:text-4xl font-bold text-white shadow-xl">
              {{ userInitials }}
            </div>

            <!-- User Info -->
            <div class="flex-1 text-center md:text-left">
              <h1 class="text-3xl md:text-4xl font-bold text-white mb-2">
                {{ user_details.full_name }}
              </h1>
              <p class="text-gray-300">{{ user_details.email }}</p>
              <p class="text-gray-400 mt-2">Member since: {{ formatDate(user_details.created_at) }}</p>
              
              <!-- Stats Cards -->
              <div class="grid grid-cols-2 gap-4 mt-6">
                <div class="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div class="text-2xl font-bold text-purple-400">{{ totalChats }}</div>
                  <div class="text-sm text-gray-300">Total Chats</div>
                </div>
                <div class="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <div class="text-2xl font-bold text-blue-400">{{ totalPapers }}</div>
                  <div class="text-sm text-gray-300">Papers Analyzed</div>
                </div>
              </div>
              <!-- Subscription Button -->
              <div class="flex-shrink-0 mt-6 md:mt-0 w-full md:w-auto flex flex-col gap-2">
                <button
                  @click="showSubscriptionModal = true"
                  class="w-full px-6 py-3 rounded-lg font-medium shadow-lg transition-all duration-200 text-lg"
                  :class="[
                    user_details.is_subscribed 
                      ? 'bg-gradient-to-r from-green-500 to-green-700 text-white hover:from-green-600 hover:to-green-800' 
                      : 'bg-gradient-to-r from-purple-500 to-purple-700 text-white hover:from-purple-600 hover:to-purple-800'
                  ]"
                >
                  {{ user_details.is_subscribed ? 'Manage Subscription' : 'Subscribe Now' }}
                </button>
                
                <!-- Add Stripe Portal Button when subscribed -->
                <button
                  v-if="user_details.is_subscribed"
                  @click="handleManageSubscription"
                  class="w-full px-6 py-3 rounded-lg text-sm font-medium bg-white/10 text-white hover:bg-white/20 transition-all duration-200"
                >
                  Billing Portal
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      <!-- Chat History Section -->
      <div class="bg-white/10 backdrop-blur-sm rounded-lg shadow-xl p-6">
        <div class="flex flex-col md:flex-row md:items-center gap-4 md:justify-between mb-6">
          <h2 class="text-2xl font-semibold text-white">Chat History</h2>
          <div class="flex flex-col md:flex-row gap-3">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Search chats..." 
              class="px-4 py-2 rounded-lg bg-white/10 text-white placeholder-gray-400 border border-gray-600/50 focus:border-purple-500 focus:ring focus:ring-purple-500/20"
            >
            <select 
              v-model="sortBy"
              class="px-4 py-2 rounded-lg bg-white/10 text-white border border-gray-600/50 focus:border-purple-500 focus:ring focus:ring-purple-500/20"
            >
              <option value="recent">Most Recent</option>
              <option value="oldest">Oldest First</option>
            </select>
          </div>
        </div>

        <!-- Chat List -->
        <div class="space-y-4 overflow-y-scroll max-h-[60vh]">
          <div v-for="chat in filteredChats" :key="chat.chat_id" 
               @click="openChat(chat.chat_id, chat.document_id)"
               class="group bg-white/5 hover:bg-white/10 backdrop-blur-sm rounded-lg p-4 transition-all duration-200 cursor-pointer">
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h3 class="text-lg font-medium text-white group-hover:text-purple-400 transition-colors">
                  {{ chat.title || 'Untitled Paper' }}
                </h3>
                <p class="text-sm text-gray-400 mt-1">Created: {{ formatDate(chat.date) }}</p>
              </div>
              <button 
                @click.stop="deleteChat(chat.chat_id)"
                class="p-2 text-gray-400 hover:text-red-400 transition-colors"
              >
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </div>

          <!-- Empty State -->
          <div v-if="filteredChats.length === 0" class="text-center py-8">
            <p class="text-gray-400">No chat history found</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <SubscriptionModal
    v-if="showSubscriptionModal"
    :show="showSubscriptionModal"
    :is-subscribed="user_details.is_subscribed"
    :subscription-data="user_details.subscription_data"
    @close="showSubscriptionModal = false"
  />
</template>

<script>
import axios from 'axios';
import { useCookies } from "vue3-cookies";
import SubscriptionModal from './SubscriptionModal.vue';

export default {
  name: 'ProfilePage',
  components: {
    SubscriptionModal
  },
  data() {
    return {
      user_details: {},
      chats: [],
      isLoading: true,
      searchQuery: '',
      sortBy: 'recent',
      error: null,
      showSubscriptionModal: false
    }
  },
  computed: {
    userInitials() {
      if (!this.user_details.full_name) return '';
      return this.user_details.full_name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase();
    },
    totalChats() {
      return this.chats.length;
    },
    totalPapers() {
      return new Set(this.chats.map(chat => chat.title)).size;
    },
    filteredChats() {
      let filtered = [...this.chats];
      
      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(chat => 
          chat.title?.toLowerCase().includes(query)
        );
      }

      // Apply sorting
      filtered.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return this.sortBy === 'recent' ? 
          dateB - dateA : 
          dateA - dateB;
      });

      return filtered;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    },
    async getUserDetails() {
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_GET_USER_DETAILS_URL}`,
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );
        this.user_details = response.data;
      } catch (error) {
        console.error('Error fetching user details:', error);
        this.error = 'Failed to load user details';
      }
    },
    async getChatHistory() {
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_GET_CHATS_URL}`,
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );
        this.chats = response.data;
      } catch (error) {
        console.error('Error fetching chat history:', error);
        this.error = 'Failed to load chat history';
      }
    },
    async deleteChat(chatId) {
      if (!confirm('Are you sure you want to delete this chat?')) return;

      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      
      try {
        await axios.delete(
          `${process.env.VUE_APP_DELETE_CHAT_URL}/${chatId}`,
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );
        // Remove the deleted chat from the local state
        this.chats = this.chats.filter(chat => chat.chat_id !== chatId);
      } catch (error) {
        console.error('Error deleting chat:', error);
        alert('Failed to delete chat');
      }
    },
    openChat(chatId, docId) {
      this.$router.push(`/paper/${chatId}?doc_id=${docId}`);
    },
    userIsLoggedIn() {
      const user = this.$cookies.get('pb_user');
      return !(user == null || user == '' || user === undefined);
    },
    async handleManageSubscription() {
      const pbUser = this.$cookies.get('pb_user');
      const pbToken = this.$cookies.get('pb_token');
      
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_CREATE_PORTAL_SESSION_URL}`,
          {},
          {
            headers: {
              'Token': `Bearer ${pbToken}`,
              'User': pbUser
            }
          }
        );
        
        // Redirect to Stripe Customer Portal
        window.location.href = response.data.url;
      } catch (error) {
        console.error('Error creating portal session:', error);
        alert('Failed to open billing portal');
      }
    }
  },
  async created() {
    if (!this.userIsLoggedIn()) {
      this.$router.push('/login');
      return;
    }

    try {
      await Promise.all([
        this.getUserDetails(),
        this.getChatHistory()
      ]);
    } catch (error) {
      console.error('Error initializing profile page:', error);
    } finally {
      this.isLoading = false;
    }
  }
}
</script>

<style scoped>
.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

/* Scrollbar styling */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}
</style> 